'use client';
import { useEffect, useMemo, useState } from 'react';
import RedeCredenciadaListPage from './rede-credenciada-listpage';
import Image from 'next/image';
import { Estabelecimento } from '@/types/rede-credenciada';

export default function RedeCredenciadaList({
  estabelecimentos,
  isLoading,
}: {
  estabelecimentos: Estabelecimento[];
  isLoading?: boolean;
}) {
  // Paginação
  const PAGE_SIZE = 4;
  const [page, setPage] = useState(1);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((estabelecimentos?.length ?? 0) / PAGE_SIZE)),
    [estabelecimentos]
  );

  // Garante que a página atual sempre seja válida quando a lista mudar
  useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  const start = (page - 1) * PAGE_SIZE;
  const pageItems = useMemo(
    () => estabelecimentos.slice(start, start + PAGE_SIZE),
    [estabelecimentos, start]
  );

  const prevDisabled = page <= 1;
  const nextDisabled = page >= totalPages;

  const goPrev = () => !prevDisabled && setPage((p) => p - 1);
  const goNext = () => !nextDisabled && setPage((p) => p + 1);

  return (
    <>
      {/* Lista de estabelecimentos: 4 por página */}
      <RedeCredenciadaListPage
        loading={isLoading}
        estabelecimentos={pageItems}
      />

      {/* Paginação */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        {/* Left */}
        <div>
          Locais encontrados:{' '}
          <b className="font-bold text-bluecaixa">{estabelecimentos.length}</b>
        </div>

        {/* Right */}
        <div className="flex flex-row gap-4 items-center">
          <button
            onClick={goPrev}
            disabled={prevDisabled}
            className={`w-[40px] h-[40px] flex items-center justify-center border border-[#BCBCBC] rounded-lg transition
              ${
                prevDisabled
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-[#DCDCDC]'
              }`}
            aria-disabled={prevDisabled}
          >
            <Image
              src="/icons/chevron-left.png"
              width={20}
              height={20}
              alt="Anterior"
            />
          </button>

          <div className="text-[#464646] text-md">
            {page}
            <span className="text-bluecaixa font-semibold mx-3">/</span>
            {totalPages}
          </div>

          <button
            onClick={goNext}
            disabled={nextDisabled}
            className={`w-[40px] h-[40px] flex items-center justify-center border border-[#BCBCBC] rounded-lg transition
              ${
                nextDisabled
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-[#DCDCDC]'
              }`}
            aria-disabled={nextDisabled}
          >
            <Image
              src="/icons/chevron-right.png"
              width={20}
              height={20}
              alt="Próxima"
            />
          </button>
        </div>
      </div>
    </>
  );
}
